import React from 'react';
import { Alert, Card, Empty, Spin, Tag, Typography } from 'antd';

import {
  SyntheticBondCurveTenorMonths,
  SyntheticBondReferenceCurveSlot,
  syntheticBondTenorLabel,
} from 'state';
import BondChartCard, { BondChartMode } from '../BondsPlayground/BondChart';
import SourceAssetPicker from './SourceAssetPicker';

type Props = {
  currencyId: number | null;
  disabled?: boolean;
  loading?: boolean;
  mode: BondChartMode;
  selectedAssetId: number | null;
  slot?: SyntheticBondReferenceCurveSlot;
  tenorMonths: SyntheticBondCurveTenorMonths;
  onSelect: (assetId: number | null) => void;
};

const SystemCurveSlot: React.FC<Props> = ({
  currencyId,
  disabled,
  loading,
  mode,
  onSelect,
  selectedAssetId,
  slot,
  tenorMonths,
}) => {
  const chart =
    slot?.status === 'available' && slot.asset
      ? {
          id: slot.asset.id,
          name: slot.asset.name,
          ric: slot.asset.ric || null,
          points: slot.points || [],
        }
      : undefined;

  return (
    <Card
      className="synthetic-bonds__curve-slot"
      size="small"
      title={
        <span className="synthetic-bonds__curve-slot-title">
          <span>{syntheticBondTenorLabel(tenorMonths)} source</span>
          <Tag color={selectedAssetId ? 'blue' : 'default'}>
            {selectedAssetId ? 'assigned' : 'unselected'}
          </Tag>
        </span>
      }
    >
      <SourceAssetPicker
        currencyId={currencyId}
        disabled={disabled}
        fallbackAsset={slot?.asset}
        initialLoader={tenorMonths === 6}
        onChange={onSelect}
        value={selectedAssetId}
      />

      <div className="synthetic-bonds__curve-slot-body">
        {loading && selectedAssetId ? (
          <div className="synthetic-bonds__centered-state">
            <Spin size="small" />
            <Typography.Text type="secondary">
              Loading selected system curve…
            </Typography.Text>
          </div>
        ) : chart ? (
          <BondChartCard bond={chart} mode={mode} />
        ) : slot && slot.status !== 'unselected' ? (
          <Alert
            message={
              slot.status === 'no_data'
                ? 'No complete DAILY curve points'
                : 'Selected source is not usable'
            }
            description={slot.reason}
            showIcon
            type={slot.status === 'no_data' ? 'info' : 'error'}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              currencyId
                ? `Choose the UnderlyingAsset declared as the ${syntheticBondTenorLabel(
                    tenorMonths,
                  )} node`
                : 'Select a currency family first'
            }
          />
        )}
      </div>
    </Card>
  );
};

export default SystemCurveSlot;
